import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { TransactionFilters } from "Components/Transactions/hooks/useTransactionFilterManager";
import {
  filtersToQueryParams,
  type TransactionFilterParams,
} from "Components/Transactions/utils/transactionFilterUtils";
import {
  getTransactionsSum,
  TransactionStatus,
  type TransactionSumResponse,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { MS_TIME } from "Utils/TimeUtils";

const TRANSACTIONS_SUMMARY_QUERY_KEY = "TRANSACTIONS_SUMMARY";

export const transactionsSummaryKeys = {
  all: [TRANSACTIONS_SUMMARY_QUERY_KEY],
  byFilters: (params: TransactionFilterParams) => [
    ...transactionsSummaryKeys.all,
    params,
  ],
};

type Props = {
  filters: TransactionFilters;
  enabled?: boolean;
};

export const useTransactionsSummary = ({ filters, enabled = true }: Props) => {
  const queryParams = useMemo(() => filtersToQueryParams(filters), [filters]);

  return useQuery<TransactionSumResponse>({
    queryKey: transactionsSummaryKeys.byFilters(queryParams),
    queryFn: processResponseWithApiProblemDetails(() =>
      getTransactionsSum(
        queryParams.dateFrom,
        queryParams.dateTo,
        [TransactionStatus.Success], // Always show only successful transactions
        true, // isCredit - incoming payments
        queryParams.payPointPublicID,
        queryParams.employeePublicID,
      ),
    ),

    enabled,
    refetchInterval: MS_TIME.halfMinute,
  });
};
